/**
 * Lock a workflow (AMO-305): its owner or an editor locks it, with an
 * optional reason ("approved for Q4"), and nobody can change it, its owner
 * included, until one of them unlocks it. It still runs. The dialog shows
 * who locked it, when and why, and the last locks and unlocks.
 *
 * @module components/workflows/WorkflowLockDialog
 */
import { WORKFLOW_LIMITS, type WorkflowLock, type WorkflowLockEvent } from "@amoreg/contracts";
import { LockIcon, LockOpenIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { setWorkflowLock } from "~/state/workflows";
import { formatRelativeTimeLabel } from "../../timestampFormat";
import { Button } from "../ui/button";
import { Dialog, DialogClose, DialogDescription, DialogFooter, DialogHeader, DialogPanel, DialogPopup, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { lockEventLabel, lockSummary } from "./WorkflowLockDialog.logic";

export function WorkflowLockDialog({
  open,
  onOpenChange,
  organizationId,
  workflowId,
  lock,
  history,
  nameOf,
  waitForSave,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly organizationId: string;
  readonly workflowId: string;
  readonly lock: WorkflowLock | null;
  readonly history: ReadonlyArray<WorkflowLockEvent>;
  /** A member's name for their address, when it is known. */
  readonly nameOf: (email: string) => string | null;
  /** Why it cannot be locked yet (this editor's own changes are not saved), or null (`lockWaitsForSave`). */
  readonly waitForSave: string | null;
}) {
  // Locking is held back while this editor's changes are unsaved; unlocking never is.
  const held = lock === null ? waitForSave : null;
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setReason("");
    setError(null);
  }, [open]);

  const change = async () => {
    setBusy(true);
    setError(null);
    const outcome = await setWorkflowLock(organizationId, workflowId, lock ? { locked: false } : { locked: true, ...(reason.trim() ? { reason: reason.trim() } : {}) });
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      return;
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (!busy ? onOpenChange(next) : undefined)}>
      <DialogPopup className="max-w-md">
        <DialogHeader>
          <DialogTitle>{lock ? "Locked" : "Lock this workflow"}</DialogTitle>
          <DialogDescription>
            {lock
              ? lockSummary(lock, nameOf)
              : "Nobody can change a locked workflow, you included, until it is unlocked. It still runs, on its schedule too. Its owner and the people who can edit it can lock and unlock it."}
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-3" data-workflow-lock-dialog="">
          {lock ? null : (
            <label className="block space-y-1.5">
              <span className="text-xs font-medium">Why (optional)</span>
              <Input
                value={reason}
                maxLength={WORKFLOW_LIMITS.lockReasonLength}
                placeholder="e.g. Approved for Q4"
                onChange={(event) => setReason(event.currentTarget.value)}
                data-workflow-lock-reason=""
              />
            </label>
          )}
          {history.length > 0 ? (
            <div className="space-y-1">
              <span className="text-xs font-medium">History</span>
              <ul className="space-y-0.5 text-[12px] text-muted-foreground" data-workflow-lock-history="">
                {history.slice(0, 10).map((event) => (
                  <li key={`${event.at}-${event.action}`}>
                    {lockEventLabel(event, nameOf)} · {formatRelativeTimeLabel(event.at)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {held ? (
            <p className="text-sm text-muted-foreground" data-workflow-lock-waits="">
              {held}
            </p>
          ) : null}
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </DialogPanel>
        <DialogFooter>
          <DialogClose render={<Button size="sm" variant="ghost" disabled={busy}>Cancel</Button>} />
          <Button size="sm" onClick={() => void change()} disabled={busy || held !== null} data-workflow-lock-save={lock ? "unlock" : "lock"}>
            {lock ? <LockOpenIcon /> : <LockIcon />}
            {busy ? "Saving…" : lock ? "Unlock" : "Lock"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
